import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../lib/toast';
import { MONTH_NAMES, dateTimeLabel } from '../../lib/format';
import type { FinSettings, FinSettingsPayload } from '../../lib/financeTypes';
import { Banner, Field, LoadingPage, Tag } from '../../components/ui';
import { usd } from './shared';

const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));
const str = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));

export function FinanceSettings() {
  const { notify, fail } = useToast();
  const state = useAsync(() => api.finSettings(), []);
  const data = state.data;

  async function guard(work: () => Promise<FinSettingsPayload>, ok: string) {
    try {
      state.setData(await work());
      notify(ok, 'success');
    } catch (error) {
      fail(error, 'That did not work.');
    }
  }

  if (!data) return state.loading ? <LoadingPage label="Loading settings…" /> : null;
  const s = data.settings;

  return (
    <div>
      <div className="card">
        <div className="card-head">
          <h3>Board confirmation</h3>
          <Tag tone={s.confirmedAt ? 'ok' : 'pend'}>{s.confirmedAt ? 'Confirmed' : 'Not confirmed'}</Tag>
        </div>
        <p className="mut">
          These settings drive expected assessments, runway and recovery scenarios. Any change needs to be confirmed again by a Board Member.
          {s.confirmedAt && ` Last confirmed by ${s.confirmedByName ?? 'a board member'} on ${dateTimeLabel(s.confirmedAt)}.`}
        </p>
        <button type="button" className="btn" disabled={Boolean(s.confirmedAt)} onClick={() => void guard(() => api.finSettingsConfirm(), 'Settings confirmed.')}>
          {s.confirmedAt ? 'Confirmed ✓' : 'Confirm these settings'}
        </button>
      </div>

      <RatesCard data={data} guard={guard} />
      <GeneralCard settings={s} guard={guard} />
      <ContractsCard data={data} guard={guard} />
      <CategoriesCard data={data} guard={guard} />
    </div>
  );
}

type Guard = (work: () => Promise<FinSettingsPayload>, ok: string) => Promise<void>;

function RatesCard({ data, guard }: { data: FinSettingsPayload; guard: Guard }) {
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [units, setUnits] = useState('');
  const [active, setActive] = useState('');
  const [dues, setDues] = useState('');
  const [note, setNote] = useState('');
  return (
    <div className="card tw">
      <div className="card-head"><h3>Homes and dues</h3></div>
      <p className="mut">
        Each entry applies from its start date until the next one, so a dues increase or new phase never rewrites history.
        For example, Pioneer Hill might start with 679 homes at $55 a month, subject to the board confirming it.
      </p>
      {data.rates.length > 0 && (
        <table>
          <thead><tr><th>Effective from</th><th className="num">Assessable homes</th><th className="num">Active homes</th><th className="num">Monthly dues</th><th className="num">Expected per month</th><th /></tr></thead>
          <tbody>
            {data.rates.map((r) => (
              <tr key={r.id}>
                <td>{r.effectiveFrom}{r.note && <div className="mut">{r.note}</div>}</td>
                <td className="num">{r.assessableUnits}</td>
                <td className="num">{r.activeUnits ?? '—'}</td>
                <td className="num">{usd(r.monthlyDues, true)}</td>
                <td className="num">{usd(r.assessableUnits * r.monthlyDues)}</td>
                <td className="num"><button type="button" className="btn tiny ghost" onClick={() => void guard(() => api.finRateDelete(r.id), 'Removed.')}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
        <Field label="Effective from"><input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} /></Field>
        <Field label="Assessable homes"><input value={units} inputMode="numeric" onChange={(e) => setUnits(e.target.value)} /></Field>
        <Field label="Active homes (optional)"><input value={active} inputMode="numeric" onChange={(e) => setActive(e.target.value)} /></Field>
        <Field label="Monthly dues ($)"><input value={dues} inputMode="decimal" onChange={(e) => setDues(e.target.value)} /></Field>
        <Field label="Note"><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} /></Field>
        <button
          type="button"
          className="btn"
          disabled={!effectiveFrom || units === '' || dues === ''}
          onClick={async () => {
            await guard(
              () => api.finRateSave({ effectiveFrom, assessableUnits: Number(units), activeUnits: numOrNull(active), monthlyDues: Number(dues), note: note.trim() || null }),
              'Saved.',
            );
            setUnits(''); setActive(''); setDues(''); setNote('');
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}

function GeneralCard({ settings, guard }: { settings: FinSettings; guard: Guard }) {
  const [f, setF] = useState(() => toForm(settings));
  useEffect(() => setF(toForm(settings)), [settings]);
  const set = (key: keyof typeof f) => (e: { target: { value: string } }) => setF((cur) => ({ ...cur, [key]: e.target.value }));

  return (
    <div className="card">
      <div className="card-head"><h3>Association settings</h3></div>
      <div className="grid two">
        <Field label="Management company"><input value={f.managementCompany} onChange={set('managementCompany')} maxLength={120} /></Field>
        <Field label="Billing frequency">
          <select value={f.billingFrequency} onChange={set('billingFrequency')}>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="semiannual">Twice a year</option>
            <option value="annual">Annually</option>
          </select>
        </Field>
        <Field label="Fiscal year starts">
          <select value={f.fiscalYearStartMonth} onChange={set('fiscalYearStartMonth')}>
            {MONTH_NAMES.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
          </select>
        </Field>
        <Field label="Minimum operating cash ($)" hint="Dashboards warn when liquidity drops below this."><input value={f.minOperatingCash} inputMode="decimal" onChange={set('minOperatingCash')} /></Field>
        <Field label="Opening operating balance ($)"><input value={f.openingOperatingBalance} inputMode="decimal" onChange={set('openingOperatingBalance')} /></Field>
        <Field label="Opening replacement balance ($)"><input value={f.openingReplacementBalance} inputMode="decimal" onChange={set('openingReplacementBalance')} /></Field>
        <Field label="Opening balances as of"><input type="date" value={f.openingBalanceDate} onChange={set('openingBalanceDate')} /></Field>
        <Field label="Planned reserve contribution per month ($)"><input value={f.reserveContributionMonthly} inputMode="decimal" onChange={set('reserveContributionMonthly')} /></Field>
        <Field label="Reserve target ($)"><input value={f.reserveTarget} inputMode="decimal" onChange={set('reserveTarget')} /></Field>
        <Field label="Expected recovery of overdue balances (%)" hint="Leave empty to assume nothing is recoverable."><input value={f.recoveryPct} inputMode="decimal" onChange={set('recoveryPct')} /></Field>
        <Field label="Recovery period (months)"><input value={f.recoveryMonths} inputMode="numeric" onChange={set('recoveryMonths')} /></Field>
        <Field label="Target collection rate (%)"><input value={f.collectionTarget} inputMode="decimal" onChange={set('collectionTarget')} /></Field>
        <Field label="Keep statements for (years)" hint="Older periods stay listed but are flagged as past retention.">
          <input value={f.retentionYears} inputMode="numeric" onChange={set('retentionYears')} />
        </Field>
      </div>
      <Field label="Reserve contribution policy"><textarea rows={2} value={f.reserveContributionPolicy} onChange={set('reserveContributionPolicy')} maxLength={1000} /></Field>
      <Field label="Assumption notes"><textarea rows={2} value={f.notes} onChange={set('notes')} maxLength={2000} /></Field>
      <Banner kind="info">Saving clears the board confirmation until someone confirms it again.</Banner>
      <button
        type="button"
        className="btn"
        style={{ marginTop: 10 }}
        onClick={() =>
          void guard(
            () =>
              api.finSettingsSave({
                managementCompany: f.managementCompany.trim() || null,
                billingFrequency: f.billingFrequency,
                fiscalYearStartMonth: Number(f.fiscalYearStartMonth),
                openingOperatingBalance: numOrNull(f.openingOperatingBalance),
                openingReplacementBalance: numOrNull(f.openingReplacementBalance),
                openingBalanceDate: f.openingBalanceDate || null,
                minOperatingCash: numOrNull(f.minOperatingCash),
                reserveContributionPolicy: f.reserveContributionPolicy.trim() || null,
                reserveContributionMonthly: numOrNull(f.reserveContributionMonthly),
                reserveTarget: numOrNull(f.reserveTarget),
                retentionYears: Number(f.retentionYears) || 7,
                assumptions: {
                  overdueRecoveryRate: f.recoveryPct.trim() === '' ? null : Number(f.recoveryPct) / 100,
                  overdueRecoveryMonths: numOrNull(f.recoveryMonths),
                  collectionRateTarget: f.collectionTarget.trim() === '' ? null : Number(f.collectionTarget) / 100,
                  notes: f.notes.trim() || null,
                },
              }),
            'Settings saved. Confirm them above when ready.',
          )
        }
      >
        Save settings
      </button>
    </div>
  );
}

function toForm(s: FinSettings) {
  const a = s.assumptions;
  return {
    managementCompany: s.managementCompany ?? '',
    billingFrequency: s.billingFrequency,
    fiscalYearStartMonth: String(s.fiscalYearStartMonth),
    openingOperatingBalance: str(s.openingOperatingBalance),
    openingReplacementBalance: str(s.openingReplacementBalance),
    openingBalanceDate: s.openingBalanceDate ?? '',
    minOperatingCash: str(s.minOperatingCash),
    reserveContributionPolicy: s.reserveContributionPolicy ?? '',
    reserveContributionMonthly: str(s.reserveContributionMonthly),
    reserveTarget: str(s.reserveTarget),
    retentionYears: String(s.retentionYears ?? 7),
    recoveryPct: a.overdueRecoveryRate === null || a.overdueRecoveryRate === undefined ? '' : String(Math.round(a.overdueRecoveryRate * 10000) / 100),
    recoveryMonths: str(a.overdueRecoveryMonths),
    collectionTarget: a.collectionRateTarget === null || a.collectionRateTarget === undefined ? '' : String(Math.round(a.collectionRateTarget * 10000) / 100),
    notes: a.notes ?? '',
  };
}

function ContractsCard({ data, guard }: { data: FinSettingsPayload; guard: Guard }) {
  const [vendorName, setVendorName] = useState('');
  const [monthly, setMonthly] = useState('');
  const [category, setCategory] = useState('');
  return (
    <div className="card tw">
      <div className="card-head"><h3>Recurring contracts</h3></div>
      <p className="mut">Optional. Used to flag months where a vendor bills more than the contract amount.</p>
      {data.contracts.length > 0 && (
        <table>
          <tbody>
            {data.contracts.map((c) => (
              <tr key={c.id}>
                <td>{c.vendorName}<div className="mut">{c.category ?? ''}</div></td>
                <td className="num">{c.monthlyAmount === null ? '—' : `${usd(c.monthlyAmount, true)} / mo`}</td>
                <td className="num"><button type="button" className="btn tiny ghost" onClick={() => void guard(() => api.finContractDelete(c.id), 'Removed.')}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
        <Field label="Vendor"><input value={vendorName} onChange={(e) => setVendorName(e.target.value)} maxLength={120} /></Field>
        <Field label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">—</option>
            {data.categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Monthly amount ($)"><input value={monthly} inputMode="decimal" onChange={(e) => setMonthly(e.target.value)} /></Field>
        <button
          type="button"
          className="btn"
          disabled={!vendorName.trim() || monthly === ''}
          onClick={async () => {
            await guard(() => api.finContractSave({ vendorName: vendorName.trim(), category: category || null, monthlyAmount: Number(monthly) }), 'Contract saved.');
            setVendorName(''); setMonthly('');
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}

function CategoriesCard({ data, guard }: { data: FinSettingsPayload; guard: Guard }) {
  const [name, setName] = useState('');
  return (
    <div className="card">
      <div className="card-head"><h3>Vendor categories</h3></div>
      <div className="chip-row">
        {data.categories.map((c) => (
          <span key={c.id} className="tag">
            {c.name}{' '}
            <button type="button" className="btn tiny ghost" aria-label={`Remove ${c.name}`} onClick={() => void guard(() => api.finCategoryDelete(c.id), 'Removed.')}>×</button>
          </span>
        ))}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <input placeholder="New category" aria-label="New category" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} style={{ maxWidth: 240 }} />
        <button type="button" className="btn" disabled={!name.trim()} onClick={async () => { await guard(() => api.finCategoryAdd(name.trim()), 'Category added.'); setName(''); }}>Add</button>
      </div>
    </div>
  );
}
